// Geometry for the table widget's PDF: how wide each column is and how many lines a
// cell wraps to. The module takes the text measurer as an argument and imports nothing,
// so `node --test` runs it without react-pdf or a font. The renderer passes
// PDF/textMeasure, which measures in the platform font.

/**
 * The lines `text` needs at `widthPx`, wrapped at spaces, greedily. A word wider than
 * the width keeps a line of its own, the same as react-pdf with hyphenation off.
 * @param {string} text
 * @param {number} widthPx
 * @param {(text: string) => number} measure the width of a string in CSS pixels
 * @returns {number}
 */
export function wrappedLines(text, widthPx, measure) {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return 1;
  const space = measure(" ");
  let lines = 1;
  let line = measure(words[0]);
  for (const word of words.slice(1)) {
    const width = measure(word);
    if (line + space + width <= widthPx) {
      line += space + width;
      continue;
    }
    lines += 1;
    line = width;
  }
  return lines;
}

/**
 * The widest word of `text`, in CSS pixels. Zero for an empty string.
 * @param {string} text
 * @param {(text: string) => number} measure
 * @returns {number}
 */
export function longestWordPx(text, measure) {
  return Math.max(0, ...text.split(/\s+/).filter(Boolean).map(measure));
}

/**
 * Column widths that share `totalPx`, the category column first.
 *
 * Every column gets at least its floor in `minPx`, its widest word plus padding, so a
 * word never runs into the next column. The category column starts at `categoryMaxPx`
 * and gives width back, down to its floor, only when the metric columns need it. The
 * width that remains goes in equal shares to the metric columns an equal share fits.
 * When the floors alone exceed `totalPx`, the widths keep the floors and the table
 * runs wider than the widget.
 * @param {number[]} minPx
 * @param {number} totalPx
 * @param {number} categoryMaxPx
 * @returns {number[]}
 */
export function fitColumnWidths(minPx, totalPx, categoryMaxPx) {
  const [categoryMin, ...metricMin] = minPx;
  const metricFloor = metricMin.reduce((sum, px) => sum + px, 0);
  const category = Math.min(categoryMaxPx, Math.max(categoryMin, totalPx - metricFloor));
  const widths = [category, ...metricMin];
  let free = totalPx - category;
  let flexible = metricMin.map((_, i) => i + 1);
  while (flexible.length > 0) {
    const share = free / flexible.length;
    const pinned = flexible.filter((i) => widths[i] > share);
    if (pinned.length === 0) {
      for (const i of flexible) widths[i] = share;
      break;
    }
    for (const i of pinned) free -= widths[i];
    flexible = flexible.filter((i) => widths[i] <= share);
  }
  return widths;
}
